import React from 'react';

import type { Fragment, TextRange } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';

import type { BlockProps, EditorContainer, InlineProps } from '../interfaces';

type FragmentRendererContextType = {
    /**
     * Returns the props that should be passed to a fragment.
     */
    getFragmentRendererProps: () => (BlockProps | InlineProps) & {
        valueDecorations: List<TextRange>;
    };

    /**
     * Returns a fragment by key from the current context.
     */
    getFragment: (fragmentKey: string) => Fragment | undefined;
};

/**
 * A FragmentRendererContext provides a function for fetching the props to pass to a
 * FragmentRenderer.
 */
export const FragmentRendererContext = React.createContext<FragmentRendererContextType>(
    // @ts-ignore null is technically not a valid value for this context, but we handle
    // the error in the Fragment renderer and it allows us to have nice typing elsewhere
    // in the app
    null
);

/**
 * A context that provides any FragmentRenderers with access to the parent Node's props.
 */
export function FragmentRendererContextProvider(props: {
    nodeProps: BlockProps | InlineProps;
    editor: EditorContainer;
    children: React.ReactNode;
}): React.ReactElement {
    const { nodeProps, editor, children } = props;

    const value = {
        getFragmentRendererProps: () => ({
            ...nodeProps,
            valueDecorations: editor.stack.getValueDecorations(editor.value),
        }),
        getFragment: (fragmentKey: string) => {
            return nodeProps.node.fragments.get(fragmentKey);
        },
    };

    return (
        <FragmentRendererContext.Provider value={value}>
            {children}
        </FragmentRendererContext.Provider>
    );
}
